import { diffDaysInclusive, parseValidDate } from '~/services/TimeService';

import type { ISODateRange } from '~/types/stopsell';

import { mergeDateRanges } from '~/utils/mergeDateRanges';

import type { ChecklistStatus } from '../../shared/checklistUtils';
import { getStatusLabel } from '../../shared/format';

import type { BlackoutChecklistRow, BlackoutCoverageSummary } from './types';

const BLACKOUT_DATES_FIELD = 'Blackout Dates';
export const BLACKOUT_COVERAGE_FIELD = 'Blackout Coverage';
export const BLACKOUT_COVERAGE_ROW_ID = 'coverage:offer';
export const FESTIVE_DATE_LABEL = 'Dec 24, Dec 25, Dec 31';

type BlackoutDateInput = App.OpportunityPage['black_out_dates'][number];

export function buildBlackoutRows({
  blackOutDates,
  hasSourceError,
  travelFrom,
  travelTo,
}: {
  blackOutDates?: Array<BlackoutDateInput>;
  hasSourceError?: boolean;
  travelFrom?: string;
  travelTo?: string;
}): Array<BlackoutChecklistRow> {
  if (hasSourceError) {
    return [
      evaluateBlackoutRow({
        id: 'blackout-source-unavailable',
        name: 'Opportunity blackout dates unavailable',
        travelFrom,
        travelTo,
      }),
    ];
  }

  return (blackOutDates ?? []).map((blackoutDate, index) =>
    evaluateBlackoutRow({
      from: blackoutDate.from__c,
      id: `date:blackout__c:${blackoutDate.sfid}`,
      name: blackoutDate.name || `Blackout ${index + 1}`,
      to: blackoutDate.to__c,
      travelFrom,
      travelTo,
    }),
  );
}

export function evaluateBlackoutRow({
  from,
  id,
  name,
  to,
  travelFrom,
  travelTo,
}: {
  from?: string;
  id: string;
  name: string;
  to?: string;
  travelFrom?: string;
  travelTo?: string;
}): BlackoutChecklistRow {
  const blackoutRange = parseDateRange(from, to);
  const travelRange = parseDateRange(travelFrom, travelTo);
  const withinTravelValidity =
    !!blackoutRange &&
    !!travelRange &&
    blackoutRange.start >= travelRange.start &&
    blackoutRange.end <= travelRange.end;
  const festiveClear = !!blackoutRange && !rangeOverlapsFestiveDates(blackoutRange);
  const status: ChecklistStatus = withinTravelValidity && festiveClear ? 'approved' : 'requires approval';

  return {
    festiveClear,
    field: BLACKOUT_DATES_FIELD,
    from,
    id,
    name,
    status,
    statusLabel: getStatusLabel(status),
    to,
    withinTravelValidity,
  };
}

export function calculateBlackoutCoverage({
  blackOutDates,
  travelFrom,
  travelTo,
}: {
  blackOutDates: Array<Pick<BlackoutChecklistRow, 'from' | 'to'>>;
  travelFrom?: string;
  travelTo?: string;
}): BlackoutCoverageSummary {
  const travelRange = parseDateRange(travelFrom, travelTo);
  if (!travelRange) {
    return {
      blackoutDays: 0,
      percent: 0,
      status: 'requires approval',
    };
  }

  const ranges = blackOutDates
    .map((blackoutDate) => parseDateRange(blackoutDate.from, blackoutDate.to))
    .filter((range): range is ISODateRange => !!range)
    .map((range) => ({
      end: range.end < travelRange.end ? range.end : travelRange.end,
      start: range.start > travelRange.start ? range.start : travelRange.start,
    }))
    .filter((range) => range.start <= range.end);
  const mergedRanges = mergeDateRanges(ranges);
  const blackoutDays = mergedRanges.reduce((sum, range) => sum + inclusiveDays(range), 0);
  const travelDays = inclusiveDays(travelRange);
  const percent = travelDays > 0 ? Math.round((blackoutDays / travelDays) * 100) : 0;

  return {
    blackoutDays,
    percent,
    status: percent < 75 ? 'approved' : 'requires approval',
    travelDays,
  };
}
export function buildBlackoutCoverageRow(coverage: BlackoutCoverageSummary): BlackoutChecklistRow {
  return {
    festiveClear: true,
    field: BLACKOUT_COVERAGE_FIELD,
    id: BLACKOUT_COVERAGE_ROW_ID,
    name: 'Blackout coverage rule',
    status: coverage.status,
    statusLabel: getStatusLabel(coverage.status),
    withinTravelValidity: true,
  };
}

function parseDateRange(from?: string, to?: string): ISODateRange | undefined {
  const start = parseDateToIsoDate(from);
  const end = parseDateToIsoDate(to);

  if (!start || !end || end < start) {
    return undefined;
  }

  return { end, start };
}

function parseDateToIsoDate(value?: string): string | undefined {
  // Date-only strings parse as local-midnight via `new Date`, which can shift a day outside UTC —
  // take the literal prefix instead of routing them through `parseValidDate`.
  const dateOnlyMatch = value?.match(/^\d{4}-\d{2}-\d{2}/);
  if (dateOnlyMatch) {
    return dateOnlyMatch[0];
  }

  return parseValidDate(value)?.toISOString().slice(0, 10);
}

function rangeOverlapsFestiveDates(range: ISODateRange): boolean {
  const startYear = Number(range.start.slice(0, 4));
  const endYear = Number(range.end.slice(0, 4));

  for (let year = startYear; year <= endYear; year += 1) {
    const festiveDays = [`${year}-12-24`, `${year}-12-25`, `${year}-12-31`];
    if (festiveDays.some((day) => day >= range.start && day <= range.end)) {
      return true;
    }
  }

  return false;
}

function inclusiveDays(range: ISODateRange): number {
  return diffDaysInclusive(range.start, range.end);
}
